import { environment } from '../config/environment';
import { useApiClient } from './api';

export interface AdminCheckResponse {
    isAdmin: boolean;
    email: string;
}

export interface User {
    id: string;
    email: string;
    firstName: string;
    lastName: string;
    createdAt: string;
    isVerified: boolean;
    status: 'active' | 'inactive' | 'pending';
    role: 'user' | 'admin';
}

export interface UserPlan {
    id: string;
    userId: string;
    name: string;
    year?: string;
    taxYear?: string;
    type: 'taxReport' | 'expertReview' | 'addon';
    package?: string;
    addonType?: string;
    status: 'active' | 'expired' | 'cancelled';
    transactionLimit?: number;
    purchaseDate: string;
    validUntil?: string;
    expiryDate?: string;
    stripePaymentId?: string;
    isAddon?: boolean;
    validated?: boolean;
    isCustomPricing?: boolean;
}

export interface UserLog {
    id: string;
    action: string;
    timestamp: string;
    details: string | null;
}

export interface UserLogsResponse {
    logs: UserLog[];
}

// REMOVED: Individual plan and "We Do Everything" interfaces
// These have been removed as part of Firebase migration

/**
 * Custom hook for admin-related functionality
 */
export const useAdminService = () => {
    const apiClient = useApiClient();

    /**
     * Check if the current user is an admin
     * @returns Promise<AdminCheckResponse>
     */
    const checkIsAdmin = async (): Promise<AdminCheckResponse> => {
        try {
            const response = await apiClient.get<AdminCheckResponse>(
                `${environment.expressApiUrl}/api/admin/check-admin`
            );
            return response.data;
        } catch (error) {
            console.error('Error checking admin status:', error);
            return { isAdmin: false, email: '' };
        }
    };

    /**
     * Get all users from the system
     * @returns Promise<User[]>
     */
    const getUsers = async (): Promise<User[]> => {
        try {
            const response = await apiClient.get<User[]>(`${environment.expressApiUrl}/api/admin/users`);
            return response.data;
        } catch (error) {
            console.error('Error fetching users:', error);
            throw error;
        }
    };

    /**
     * Get user plans
     * @param userId - The user ID
     * @returns Promise<UserPlan[]>
     */
    const getUserPlans = async (userId: string): Promise<UserPlan[]> => {
        try {
            const response = await apiClient.get<{ plans: UserPlan[] }>(
                `${environment.expressApiUrl}/api/admin/users/${userId}/plans`
            );
            return response.data.plans;
        } catch (error) {
            console.error('Error fetching user plans:', error);
            throw error;
        }
    };

    /**
     * Get user activity logs
     * @param userId - The user ID
     * @returns Promise<UserLog[]>
     */
    const getUserLogs = async (userId: string): Promise<UserLog[]> => {
        try {
            const response = await apiClient.get<UserLogsResponse>(
                `${environment.expressApiUrl}/api/admin/users/${userId}/logs`
            );
            return response.data.logs;
        } catch (error) {
            console.error('Error fetching user logs:', error);
            throw error;
        }
    };

    /**
     * Get a custom token to login as a specific user
     * @param userId - The user ID to login as
     * @returns Promise<string> - The custom token
     */
    const loginAsUser = async (userId: string): Promise<string> => {
        try {
            const response = await apiClient.post<{ customToken: string }>(
                `${environment.expressApiUrl}/api/admin/users/${userId}/login-as`
            );
            return response.data.customToken;
        } catch (error) {
            console.error('Error generating login token:', error);
            throw error;
        }
    };

    // REMOVED: validateWeDoEverything, getIndividualPlan, setIndividualPlan, deleteIndividualPlan
    // These have been removed as part of Firebase migration

    return {
        checkIsAdmin,
        getUsers,
        getUserPlans,
        getUserLogs,
        loginAsUser
    };
};
